import { Download, FileImage, FileSpreadsheet } from 'lucide-react'

import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InsightsIncomeSkeleton,
  SectionHeading,
} from '@/components/ui'
import { useReconciliationEntitled } from '@/modules/account'
import {
  exportCoverageCSV,
  exportCoveragePNG,
  StatementCoverage,
  useFetchProject,
  useFetchTransactions,
  useLoadingStatements,
  useProject,
  useStatementCoverage,
} from '@/modules/projects'

interface CoveragePageProps {
  slug: string
}

// Built from statements already in the store, so no generation pass is needed.
// Clicking a covered month filters the ledger and jumps to Transactions (inside StatementCoverage).
export function CoveragePage({ slug }: CoveragePageProps) {
  useFetchProject(slug)
  useFetchTransactions(slug)

  const project = useProject()
  const statementCoverage = useStatementCoverage()
  const isLoading = useLoadingStatements()
  const isReconciliationEntitled = useReconciliationEntitled()

  const handleExportPNG = () => {
    if (statementCoverage) {
      exportCoveragePNG(statementCoverage, {
        projectName: project?.name,
        slug,
        includeReconciliation: isReconciliationEntitled,
      })
    }
  }

  const handleExportCSV = () => {
    if (statementCoverage) {
      exportCoverageCSV(statementCoverage.accounts, slug, {
        includeReconciliation: isReconciliationEntitled,
      })
    }
  }

  if (isLoading) return <InsightsIncomeSkeleton />

  if (!statementCoverage) {
    return (
      <div className="rounded-lg border bg-white p-8 text-center text-sm text-muted-foreground">
        No statement coverage yet. Upload statements on the Files tab to see
        coverage across accounts and months.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <SectionHeading
        title="Statement coverage"
        description="Which months each account has statements for, and where the gaps are."
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button theme="outline" size="sm">
                <Download className="h-4 w-4" />
                Export coverage
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPNG}>
                <FileImage className="h-4 w-4" />
                Export as PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileSpreadsheet className="h-4 w-4" />
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <Card>
        <CardContent className="p-0">
          <StatementCoverage slug={slug} />
        </CardContent>
      </Card>
    </div>
  )
}
